import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	ArrowRightCircle,
	Lock,
	LockKeyhole,
	Mail,
	Star,
	UserPlus,
} from 'lucide-react';
import { Button, GoogleButton, Input, InputWrapperAuth } from '@/components/ui';
import { useLoginUser } from '@/services';
import { redirectToOAuthLogin } from '@/utils/logoutBeforeLogin';
import { markMatchReturnPending } from '@/utils/matchHandoff';

const loginSchema = z.object({
	email: z.string().max(255, 'Email inválido').email('Email inválido'),
	password: z.string().min(1, 'A senha é obrigatória'),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface MatchAuthStepProps {
	/** Chamado após login bem-sucedido (a página envia as respostas em seguida). */
	onAuthenticated: () => void;
	totalAnswered: number;
}

const TEASER_CARD = {
	hidden: { opacity: 0, y: 18, scale: 0.96 },
	show: (i: number) => ({
		opacity: 1,
		y: 0,
		scale: 1,
		transition: {
			delay: 0.2 + i * 0.12,
			type: 'spring' as const,
			stiffness: 180,
			damping: 18,
		},
	}),
};

/**
 * Último passo antes do resultado: gatilho ("encontramos 3 terapeutas para o seu perfil")
 * + entrar ou criar conta. As respostas ficam guardadas localmente e são enviadas
 * assim que a sessão existir.
 */
const MatchAuthStep = ({
	onAuthenticated,
	totalAnswered,
}: MatchAuthStepProps) => {
	const navigate = useNavigate();
	const [mode, setMode] = useState<'choose' | 'login'>('choose');

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm<LoginFormData>({ resolver: zodResolver(loginSchema) });

	const { mutateAsync: login, isPending } = useLoginUser(() =>
		onAuthenticated(),
	);

	const onLoginSubmit = async (data: LoginFormData) => {
		await login(data);
	};

	const goToRegister = () => {
		markMatchReturnPending();
		navigate('/cadastro');
	};

	const goToGoogle = () => {
		markMatchReturnPending();
		const baseUrl = import.meta.env.VITE_BACKEND_URL.replace(/\/$/, '');
		void redirectToOAuthLogin(`${baseUrl}/user/login/oauth`);
	};

	const iconCommonClass = 'w-4 h-4 text-zinc-500';

	return (
		<div className="flex w-full flex-1 flex-col justify-center gap-8 py-4">
			<div className="space-y-3">
				<motion.p
					initial={{ opacity: 0, y: 6 }}
					animate={{ opacity: 1, y: 0 }}
					className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.2em] text-brand-pink"
				>
					<Star className="h-3.5 w-3.5 fill-current" />
					Match concluído · {totalAnswered} respostas analisadas
				</motion.p>
				<motion.h1
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.08 }}
					className="text-3xl font-black leading-[1.05] tracking-tight text-stone-900 md:text-[2.5rem]"
				>
					Encontramos 3 terapeutas para o seu perfil.
				</motion.h1>
				<motion.p
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.16 }}
					className="max-w-xl text-base font-medium text-stone-500 md:text-lg"
				>
					Eles já estão separados e na ordem de afinidade com o que
					você contou. Para ver quem são e agendar, crie sua conta ou
					entre. Leva menos de um minuto e o seu resultado fica salvo.
				</motion.p>
			</div>

			{/* Teaser: 3 cards bloqueados para gerar curiosidade */}
			<div className="grid gap-3 sm:grid-cols-3">
				{[1, 2, 3].map((rank, index) => (
					<motion.div
						key={rank}
						custom={index}
						variants={TEASER_CARD}
						initial="hidden"
						animate="show"
						className="relative rounded-[1.5rem] border border-stone-200 bg-white p-4 pt-6"
					>
						<span className="absolute -top-2.5 left-4 inline-flex items-center gap-1 rounded-full bg-brand-pink px-2.5 py-0.5 text-[9px] font-black uppercase tracking-[0.16em] text-white shadow-sm">
							<Star className="h-2.5 w-2.5 fill-current" />
							{rank}º match
						</span>
						<div className="flex items-center gap-3">
							<span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-stone-100 text-stone-400">
								<LockKeyhole className="h-5 w-5" />
							</span>
							<div className="min-w-0 flex-1 space-y-2">
								<span className="block h-3 w-3/4 rounded-full bg-stone-200 blur-[2px]" />
								<span className="block h-2.5 w-1/2 rounded-full bg-stone-100 blur-[2px]" />
							</div>
						</div>
						<div className="mt-4 space-y-1.5">
							<span className="block h-2 w-full rounded-full bg-stone-100 blur-[1px]" />
							<span className="block h-2 w-5/6 rounded-full bg-stone-100 blur-[1px]" />
						</div>
					</motion.div>
				))}
			</div>

			<motion.div
				key={mode}
				initial={{ opacity: 0, y: 10 }}
				animate={{ opacity: 1, y: 0 }}
				className="mx-auto w-full max-w-md rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm md:p-8"
			>
				{mode === 'choose' ? (
					<div className="space-y-3">
						<button
							type="button"
							onClick={goToRegister}
							className="flex w-full items-center justify-center gap-2 rounded-xl bg-stone-900 px-4 py-3.5 text-sm font-bold text-white transition-colors hover:bg-stone-800"
						>
							<UserPlus className="h-4 w-4" />
							Criar conta e ver meus 3 terapeutas
						</button>
						<GoogleButton
							className="w-full max-w-none rounded-xl border-stone-200 py-2.5 text-xs hover:bg-stone-50"
							onClick={goToGoogle}
						/>
						<div className="relative flex items-center py-1">
							<div className="flex-grow border-t border-stone-100" />
							<span className="mx-3 text-[10px] font-black uppercase tracking-widest text-stone-300">
								ou
							</span>
							<div className="flex-grow border-t border-stone-100" />
						</div>
						<button
							type="button"
							onClick={() => setMode('login')}
							className="w-full rounded-xl border border-stone-200 px-4 py-3 text-sm font-bold text-stone-700 transition-colors hover:border-stone-900 hover:text-stone-900"
						>
							Já tenho conta, quero entrar
						</button>
					</div>
				) : (
					<form
						onSubmit={handleSubmit(onLoginSubmit)}
						className="space-y-4"
					>
						<div className="space-y-1">
							<label className="ml-1 text-[9px] font-black uppercase tracking-widest text-zinc-500">
								E-mail
							</label>
							<InputWrapperAuth
								className="max-w-none"
								icon={<Mail className={iconCommonClass} />}
								error={errors.email?.message}
								value={watch('email')}
								displayError
							>
								<Input
									{...register('email')}
									placeholder="nome@exemplo.com"
									autoComplete="email"
									className="h-10 border-zinc-200 text-sm focus:border-zinc-400"
								/>
							</InputWrapperAuth>
						</div>
						<div className="space-y-1">
							<label className="ml-1 text-[9px] font-black uppercase tracking-widest text-zinc-500">
								Senha
							</label>
							<InputWrapperAuth
								className="max-w-none"
								icon={<Lock className={iconCommonClass} />}
								error={errors.password?.message}
								value={watch('password')}
								displayError
							>
								<Input
									type="password"
									{...register('password')}
									placeholder="********"
									autoComplete="current-password"
									className="h-10 border-zinc-200 text-sm focus:border-zinc-400"
								/>
							</InputWrapperAuth>
						</div>
						<Button
							type="submit"
							disabled={isPending}
							className="w-full justify-center rounded-xl border-none bg-stone-900 py-3 text-sm font-bold text-white transition-all hover:bg-stone-800 disabled:opacity-60"
							text={
								isPending
									? 'Entrando...'
									: 'Entrar e ver meus 3 terapeutas'
							}
							endIcon={
								<ArrowRightCircle className="ml-1 h-4 w-4" />
							}
						/>
						<div className="flex items-center justify-between text-[11px] font-medium text-stone-400">
							<button
								type="button"
								onClick={() => setMode('choose')}
								className="hover:text-stone-900"
							>
								Voltar
							</button>
							<button
								type="button"
								onClick={() => navigate('/recuperar-senha')}
								className="hover:text-stone-900"
							>
								Esqueci minha senha
							</button>
						</div>
					</form>
				)}
			</motion.div>

			<p className="text-center text-xs font-medium text-stone-400">
				Suas respostas ficam salvas neste navegador até você entrar.
				Nada é enviado antes disso.
			</p>
		</div>
	);
};

export { MatchAuthStep };
